import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FaArrowLeft,
  FaBriefcase,
  FaBuilding,
  FaCheckCircle,
  FaChartLine,
  FaChevronDown,
  FaChevronUp,
  FaCreditCard,
  FaDatabase,
  FaLock,
  FaRocket,
  FaShieldAlt,
  FaSpinner,
  FaUserShield,
  FaUserTie,
  FaUsers,
} from "react-icons/fa";
import { getDashboardPathForRole, normalizeRole } from "../utils/authRole";
import {
  getSubscriptionStatus,
  initiateSubscriptionPayment,
  verifyPayment,
} from "../api/payments";
import { useTenant } from "../context/TenantContext";
import { useAppTheme } from "../context/AppThemeContext";
import { formatInr, getSubscriptionPlan, SUBSCRIPTION_PLANS } from "../data/subscriptionPlans";

const CODE_PATTERN = /^[A-Z0-9][A-Z0-9_-]{2,19}$/;

const PLAN_ICONS = {
  ONE_MONTH: FaRocket,
  THREE_MONTH: FaChartLine,
  SIX_MONTH: FaBriefcase,
  ONE_YEAR: FaBuilding,
};

const LIMIT_ICONS = {
  Students: FaUsers,
  "Staff members": FaUserTie,
  "Admin users": FaUserShield,
  Storage: FaDatabase,
};

const normalizeCode = (value = "") => String(value)
  .toUpperCase()
  .replace(/[^A-Z0-9_-]/g, "")
  .slice(0, 20);

const loadRazorpayScript = () => new Promise((resolve, reject) => {
  if (window.Razorpay) {
    resolve(true);
    return;
  }

  const existingScript = document.querySelector('script[data-razorpay-sdk="true"]');
  if (existingScript) {
    existingScript.addEventListener("load", () => resolve(true), { once: true });
    existingScript.addEventListener("error", () => reject(new Error("Payment gateway could not be loaded.")), { once: true });
    return;
  }

  const script = document.createElement("script");
  script.src = "https://checkout.razorpay.com/v1/checkout.js";
  script.async = true;
  script.dataset.razorpaySdk = "true";
  script.onload = () => resolve(true);
  script.onerror = () => reject(new Error("Payment gateway could not be loaded. Check your connection."));
  document.body.appendChild(script);
});

const getCurrentUserRole = () => {
  try {
    const saved = localStorage.getItem("user");
    if (!saved) return "";
    let parsed = saved;
    try {
      parsed = JSON.parse(saved);
    } catch {
      // Login stores the normalized role as a plain string.
    }
    const raw = parsed?.role ?? parsed;
    return normalizeRole(typeof raw === "string" ? raw : String(raw));
  } catch {
    return "";
  }
};

const toDisplayAmount = (value, fallback = 0) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric < 0) return fallback;
  // Razorpay order amounts are commonly returned in paise.
  return numeric > 100000 && numeric % 100 === 0 ? numeric / 100 : numeric;
};

const buildPricing = (order, plan, couponCode) => {
  const originalAmount = toDisplayAmount(
    order?.originalAmount ?? order?.baseAmount ?? order?.planAmount,
    plan.price
  );
  const gstAmount = toDisplayAmount(
    order?.gstAmount ?? order?.taxAmount ?? order?.gst,
    0
  );
  const discountAmount = toDisplayAmount(order?.discountAmount, 0);
  const finalRaw = order?.finalAmount
    ?? order?.payableAmount
    ?? order?.totalAmount
    ?? order?.amount;
  const finalAmount = finalRaw === undefined
    ? Math.max(0, originalAmount - discountAmount + gstAmount)
    : toDisplayAmount(finalRaw, Math.max(0, originalAmount - discountAmount + gstAmount));

  return {
    originalAmount,
    discountAmount,
    gstAmount,
    finalAmount,
    couponCode: order?.couponCode || order?.appliedCouponCode || couponCode || "",
  };
};

const SummaryRow = ({ label, value, strong = false, isDarkMode = false }) => (
  <div className="flex items-center justify-between gap-4 py-2.5">
    <span className={isDarkMode ? "text-sm text-slate-400" : "text-sm text-slate-500"}>{label}</span>
    <span className={strong ? (isDarkMode ? "text-base font-black text-white" : "text-base font-black text-slate-950") : (isDarkMode ? "text-sm font-semibold text-slate-200" : "text-sm font-semibold text-slate-800")}>
      {value}
    </span>
  </div>
);

const SubscriptionPayment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { activeTenant, refreshSubscriptionStatus } = useTenant();
  const { isDarkMode } = useAppTheme();
  const cancelledRef = useRef(false);
  const queryPlan = new URLSearchParams(location.search).get("plan");
  const hasValidPlan = SUBSCRIPTION_PLANS.some((plan) => plan.value === queryPlan);
  const selectedPlan = useMemo(() => getSubscriptionPlan(queryPlan), [queryPlan]);
  const SelectedPlanIcon = PLAN_ICONS[selectedPlan.value] || FaCreditCard;
  const [couponOpen, setCouponOpen] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [couponApplied, setCouponApplied] = useState(false);
  const [paymentState, setPaymentState] = useState("idle");
  const [pricing, setPricing] = useState(() => buildPricing(null, selectedPlan, ""));

  useEffect(() => () => {
    cancelledRef.current = true;
  }, []);

  useEffect(() => {
    if (!hasValidPlan) navigate("/subscription-plans", { replace: true });
  }, [hasValidPlan, navigate]);

  useEffect(() => {
    setPricing(buildPricing(null, selectedPlan, couponCode));
  }, [selectedPlan, couponCode]);

  const isBusy = ["loading", "checkout", "verifying"].includes(paymentState);

  const goBack = () => navigate("/subscription-plans");

  const handleApplyCoupon = () => {
    if (!couponCode) {
      setCouponApplied(false);
      toast.info("Coupon codes are optional. You can continue without one.");
      return;
    }
    if (!CODE_PATTERN.test(couponCode)) {
      toast.error("Enter a valid coupon code using 3–20 letters, numbers, _ or -.");
      return;
    }
    setCouponApplied(true);
    toast.success("Coupon added. The final discount will be confirmed securely at checkout.");
  };

  const confirmPayment = async (razorpayResponse) => {
    setPaymentState("verifying");
    try {
      try {
        await verifyPayment(razorpayResponse);
      } catch (verifyError) {
        const errorCode = verifyError?.response?.data?.errorCode
          || verifyError?.response?.data?.error_code
          || "";
        if (errorCode !== "SUBSCRIPTION_EXPIRED") throw verifyError;
      }

      for (let attempt = 1; attempt <= 8; attempt += 1) {
        if (cancelledRef.current) return;
        try {
          const status = await getSubscriptionStatus();
          const statusValue = String(status?.status || status?.subscriptionStatus || "").toUpperCase();
          if (status?.active === true || (statusValue === "ACTIVE" && status?.expired !== true)) {
            await refreshSubscriptionStatus();
            setPaymentState("success");
            toast.success("Payment confirmed. Your access has been restored.");
            const role = getCurrentUserRole();
            const dashboardPath = getDashboardPathForRole(role);
            navigate(dashboardPath && dashboardPath !== "/" ? dashboardPath : "/", { replace: true });
            return;
          }
        } catch {
          // The status endpoint is retried while the provider settles the payment.
        }
        if (attempt < 8) await new Promise((resolve) => setTimeout(resolve, 2500));
      }

      setPaymentState("idle");
      toast.info("Payment received. Activation is still processing; please check your status again shortly.", { autoClose: 6500 });
    } catch (error) {
      if (cancelledRef.current) return;
      setPaymentState("idle");
      toast.error(error?.message || "Payment verification failed. Please contact support.");
    }
  };

  const handlePayNow = async () => {
    if (couponCode && !CODE_PATTERN.test(couponCode)) {
      toast.error("Enter a valid coupon code or remove it before continuing.");
      return;
    }

    setPaymentState("loading");
    try {
      await loadRazorpayScript();
      const order = await initiateSubscriptionPayment({
        plan: selectedPlan.value,
        couponCode: couponApplied ? couponCode : "",
      });
      if (cancelledRef.current) return;

      setPricing(buildPricing(order, selectedPlan, couponApplied ? couponCode : ""));
      const orderId = order?.id || order?.orderId || order?.order_id || order?.razorpayOrderId || "";
      const key = order?.key
        || order?.keyId
        || order?.key_id
        || order?.razorpayKeyId
        || process.env.REACT_APP_RAZORPAY_KEY
        || "";

      if (!orderId) {
        setPaymentState("manual");
        return;
      }
      if (!key) throw new Error("Payment gateway configuration is missing. Please contact support.");

      setPaymentState("checkout");
      const razorpay = new window.Razorpay({
        key,
        amount: order.amount,
        currency: order.currency || "INR",
        name: "ClassNX Platform",
        description: `${selectedPlan.duration} renewal for ${activeTenant?.name || "your school"}`,
        order_id: orderId,
        handler: confirmPayment,
        modal: {
          ondismiss: () => {
            if (!cancelledRef.current) setPaymentState("idle");
          },
        },
        theme: { color: "#2563eb" },
      });
      razorpay.open();
    } catch (error) {
      if (cancelledRef.current) return;
      setPaymentState("idle");
      toast.error(error?.message || "Unable to start secure checkout. Please try again.");
    }
  };

  const handleCheckManualPayment = async () => {
    setPaymentState("verifying");
    const status = await refreshSubscriptionStatus();
    if (status?.active === true) {
      const role = getCurrentUserRole();
      navigate(getDashboardPathForRole(role), { replace: true });
      return;
    }
    setPaymentState("manual");
    toast.info("We have not received confirmation yet. Please try again shortly.");
  };

  const card = isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white";
  const page = isDarkMode ? "min-h-full bg-slate-950 px-4 py-8 text-slate-100 sm:px-6 lg:px-10" : "min-h-full bg-slate-50 px-4 py-8 text-slate-900 sm:px-6 lg:px-10";

  return (
    <div className={page}>
      <div className="mx-auto max-w-6xl">
        <button type="button" onClick={goBack} className={isDarkMode ? "mb-6 inline-flex items-center gap-2 text-sm font-bold text-slate-300 transition hover:text-white" : "mb-6 inline-flex items-center gap-2 text-sm font-bold text-slate-600 transition hover:text-blue-700"}>
          <FaArrowLeft className="text-xs" /> Back to plans
        </button>

        <div className="mb-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600">Secure payment</p>
          <h1 className="mt-2 text-3xl font-black tracking-tight sm:text-4xl">Review and complete your renewal.</h1>
          <p className={isDarkMode ? "mt-3 max-w-2xl text-sm leading-6 text-slate-400" : "mt-3 max-w-2xl text-sm leading-6 text-slate-600"}>
            Your plan is selected. Review the breakdown, optionally add a coupon, then continue to the secure payment gateway.
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
          <section className={`rounded-[2rem] border p-6 shadow-xl shadow-slate-900/5 sm:p-8 ${card}`}>
            <div className="flex items-start justify-between gap-4 border-b border-slate-200/70 pb-6 dark:border-slate-800">
              <div className="flex items-start gap-4">
                <div className={isDarkMode ? "flex h-12 w-12 shrink-0 items-center justify-center border border-teal-800 bg-teal-950/40 text-teal-300" : "flex h-12 w-12 shrink-0 items-center justify-center border border-teal-200 bg-teal-50 text-teal-700"}>
                  <SelectedPlanIcon aria-hidden="true" />
                </div>
                <div>
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">Selected plan</p>
                <h2 className="mt-2 text-3xl font-black">{selectedPlan.name}</h2>
                <p className="mt-2 text-sm text-slate-500">{selectedPlan.duration} · For {activeTenant?.name || "your school"}</p>
                </div>
              </div>
              <div className={isDarkMode ? "border border-slate-700 bg-slate-950 p-3 text-slate-300" : "border border-slate-200 bg-slate-50 p-3 text-slate-700"}><FaCreditCard /></div>
            </div>

            <div className={isDarkMode ? "mt-6 border border-slate-800 bg-slate-950/40 p-5" : "mt-6 border border-slate-200 bg-slate-50/70 p-5"}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-blue-600">Plan details</p>
                  <p className={isDarkMode ? "mt-2 text-sm leading-6 text-slate-300" : "mt-2 text-sm leading-6 text-slate-600"}>{selectedPlan.description}</p>
                </div>
                <span className={isDarkMode ? "shrink-0 text-sm font-bold text-teal-300" : "shrink-0 text-sm font-bold text-teal-700"}>{selectedPlan.savings}</span>
              </div>
              <div className="mt-5 grid grid-cols-2 gap-px border border-slate-200 bg-slate-200 sm:grid-cols-4 dark:border-slate-800 dark:bg-slate-800">
                {selectedPlan.limits.map((limit) => {
                  const LimitIcon = LIMIT_ICONS[limit.label] || FaUsers;
                  return (
                    <div key={limit.label} className={isDarkMode ? "bg-slate-900 p-3" : "bg-white p-3"}>
                      <p className={isDarkMode ? "flex items-center gap-2 text-xs font-semibold text-slate-400" : "flex items-center gap-2 text-xs font-semibold text-slate-500"}>
                        <LimitIcon className="text-teal-600" aria-hidden="true" /> {limit.label}
                      </p>
                      <p className={isDarkMode ? "mt-1 text-lg font-black text-slate-100" : "mt-1 text-lg font-black text-slate-900"}>{limit.value}</p>
                    </div>
                  );
                })}
              </div>
              <div className="mt-5 border-t border-slate-200 pt-4 dark:border-slate-800">
                <p className={isDarkMode ? "text-xs font-bold uppercase tracking-[0.16em] text-slate-400" : "text-xs font-bold uppercase tracking-[0.16em] text-slate-500"}>Included features</p>
                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  {selectedPlan.features.map((feature) => (
                    <p key={feature} className={isDarkMode ? "flex items-start gap-2 text-sm text-slate-300" : "flex items-start gap-2 text-sm text-slate-600"}>
                      <FaCheckCircle className="mt-0.5 shrink-0 text-emerald-600" aria-hidden="true" /> {feature}
                    </p>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-6 rounded-2xl border border-slate-200/70 bg-slate-50/70 px-4 py-2 dark:border-slate-800 dark:bg-slate-950/40">
              <SummaryRow label="Original price" value={formatInr(pricing.originalAmount)} isDarkMode={isDarkMode} />
              <SummaryRow label="Discount" value={pricing.discountAmount > 0 ? `− ${formatInr(pricing.discountAmount)}` : formatInr(0)} isDarkMode={isDarkMode} />
              {pricing.couponCode && <SummaryRow label="Coupon" value={pricing.couponCode} isDarkMode={isDarkMode} />}
              <SummaryRow label="GST" value={pricing.gstAmount > 0 ? formatInr(pricing.gstAmount) : "Calculated at checkout"} isDarkMode={isDarkMode} />
              <div className="my-2 border-t border-slate-200 dark:border-slate-800" />
              <SummaryRow label="Final amount" value={formatInr(pricing.finalAmount)} strong isDarkMode={isDarkMode} />
            </div>

            <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-800">
              <button type="button" onClick={() => setCouponOpen((current) => !current)} className={isDarkMode ? "flex w-full items-center justify-between px-4 py-4 text-left text-sm font-bold text-slate-200 hover:bg-slate-800/60" : "flex w-full items-center justify-between px-4 py-4 text-left text-sm font-bold text-slate-700 hover:bg-slate-50"} aria-expanded={couponOpen}>
                <span>Have a coupon code?</span>
                {couponOpen ? <FaChevronUp className="text-xs" /> : <FaChevronDown className="text-xs" />}
              </button>
              {couponOpen && (
                <div className={isDarkMode ? "border-t border-slate-800 bg-slate-950/40 p-4" : "border-t border-slate-200 bg-slate-50/70 p-4"}>
                  <div className="flex flex-col gap-3 sm:flex-row">
                    <input type="text" value={couponCode} onChange={(event) => { setCouponCode(normalizeCode(event.target.value)); setCouponApplied(false); }} placeholder="Enter coupon" className={isDarkMode ? "min-w-0 flex-1 rounded-xl border border-slate-700 bg-slate-900 px-3 py-2.5 text-sm uppercase tracking-wide text-slate-100 outline-none focus:border-blue-500" : "min-w-0 flex-1 rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm uppercase tracking-wide text-slate-800 outline-none focus:border-blue-500"} />
                    <button type="button" onClick={handleApplyCoupon} className="rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-blue-700">Apply</button>
                  </div>
                  {couponApplied && <p className="mt-2 flex items-center gap-2 text-xs font-semibold text-emerald-600"><FaCheckCircle /> Coupon will be sent with your payment request.</p>}
                </div>
              )}
            </div>

            <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-2 text-xs text-slate-500"><FaShieldAlt className="text-emerald-600" /> Secure payment processed by Razorpay</div>
              <button type="button" onClick={handlePayNow} disabled={isBusy || paymentState === "success"} className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-blue-700 to-teal-600 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-blue-700/20 transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60">
                {isBusy ? <FaSpinner className="animate-spin" /> : <FaLock />}
                {paymentState === "verifying" ? "Verifying payment…" : paymentState === "checkout" ? "Checkout open" : "Pay now"}
              </button>
            </div>
          </section>

          <aside className={`h-fit rounded-[2rem] border p-6 shadow-xl shadow-slate-900/5 sm:p-7 ${card}`}>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-blue-600">Payment steps</p>
            <ol className="mt-5 space-y-5">
              {[
                ["01", "Review your plan", "Confirm duration and final amount."],
                ["02", "Optional coupon", "Add a code only if you have one."],
                ["03", "Secure checkout", "Complete payment in the gateway."],
                ["04", "Access restored", "We verify payment before redirecting."],
              ].map(([number, title, description]) => (
                <li key={number} className="flex gap-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-50 text-xs font-black text-blue-700">{number}</span>
                  <span><span className="block text-sm font-bold">{title}</span><span className="mt-1 block text-xs leading-5 text-slate-500">{description}</span></span>
                </li>
              ))}
            </ol>
            {paymentState === "manual" && (
              <div className="mt-7 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                <p className="font-bold">Payment request created</p>
                <p className="mt-1 text-xs leading-5">Your payment provider has not returned a checkout order. Check again after completing the payment through your configured channel.</p>
                <button type="button" onClick={handleCheckManualPayment} className="mt-3 rounded-full bg-amber-600 px-4 py-2 text-xs font-bold text-white">Check payment status</button>
              </div>
            )}
            <div className="mt-7 flex items-start gap-3 rounded-2xl bg-slate-50 p-4 text-xs leading-5 text-slate-500 dark:bg-slate-950/50">
              <FaShieldAlt className="mt-0.5 shrink-0 text-emerald-600" /> Your card or UPI details are handled by the payment provider and are never stored by ClassNX.
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPayment;
